import { driver } from 'driver.js'
import { onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { ensureSetupState, isSetupApplicable } from '@/composables/setupState'
import { createTourQueue } from '@/composables/tourQueue'
import { posthog } from '@/composables/usePosthog'
import { availableSteps, chooseBranch, progressSteps, stepsForBranch, tours } from '@/config/tours'
import { currentPersonId, inStaffSession } from '@/services/analyticsIdentity'
import api from '@/store/api'

const SEEN_STORAGE_PREFIX = 'bottle_tour_seen:'
const DESKTOP_QUERY = '(min-width: 1280px)'
const TARGET_WAIT_MS = 15000
const PREVIEW_PARAM = 'tour'
const HIGHLIGHT_SETTLE_MS = 500
const START_POLL_MS = 500

const seenKey = (tour, personId) => `${SEEN_STORAGE_PREFIX}${tour.id}:${personId}`

export const hasSeenTour = (tour, personId) => localStorage.getItem(seenKey(tour, personId)) !== null

function markTourSeen(tour, personId) {
  const seenAt = new Date().toISOString()
  localStorage.setItem(seenKey(tour, personId), seenAt)
  if (posthog.__loaded) {
    posthog.setPersonProperties({ [`product_tour_${tour.id}_seen`]: true, [`product_tour_${tour.id}_seen_at`]: seenAt })
  }
}

const isVisible = element => element.getClientRects().length > 0

const findTarget = step => (step?.element ? [...document.querySelectorAll(step.element)].find(isVisible) ?? null : null)

const isUncovered = element => {
  const { left, top, width, height } = element.getBoundingClientRect()
  return element.contains(document.elementFromPoint(left + width / 2, top + height / 2))
}

export const activeTour = ref(null)

const tourQueue = createTourQueue({ order: tours, isBusy: () => activeTour.value !== null })

const stepsForViewport = tour => (window.matchMedia(DESKTOP_QUERY).matches ? tour.steps : tour.mobileSteps)

const canStart = tour => tour.steps.some(step => findTarget(step) && isUncovered(findTarget(step)))

function waitUntil(isReady) {
  return new Promise(resolve => {
    const startedAt = Date.now()
    const check = () => {
      if (isReady() || Date.now() - startedAt > TARGET_WAIT_MS) return resolve()
      requestAnimationFrame(check)
    }
    check()
  })
}

const waitForTarget = step => waitUntil(() => !step?.element || findTarget(step))

const rectKey = element => {
  const { x, y, width, height } = element.getBoundingClientRect()
  return `${x},${y},${width},${height}`
}

function showWaiting(waiting) {
  const popover = document.querySelector('.driver-popover')
  const nextButton = popover?.querySelector('.driver-popover-next-btn')
  if (!popover || !nextButton) return
  popover.classList.toggle('bottle-tour--waiting', waiting)
  if (waiting) nextButton.dataset.label = nextButton.textContent
  nextButton.textContent = waiting ? 'Loading…' : nextButton.dataset.label ?? nextButton.textContent
}

const CURSOR_ICON =
  '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M10 1a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 10 1ZM5.05 3.05a.75.75 0 0 1 1.06 0l1.062 1.06A.75.75 0 1 1 6.11 5.173L5.05 4.11a.75.75 0 0 1 0-1.06ZM14.95 3.05a.75.75 0 0 1 0 1.06l-1.06 1.062a.75.75 0 0 1-1.062-1.061l1.061-1.06a.75.75 0 0 1 1.06 0ZM3 8a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5A.75.75 0 0 1 3 8ZM14 8a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5A.75.75 0 0 1 14 8ZM7.172 10.828a.75.75 0 0 1 0 1.061L6.11 12.95a.75.75 0 0 1-1.06-1.06l1.06-1.06a.75.75 0 0 1 1.06 0ZM10.766 7.51a.75.75 0 0 0-1.37.365l-.492 6.861a.75.75 0 0 0 1.204.65l1.043-.799.985 3.678a.75.75 0 0 0 1.45-.388l-.978-3.646 1.292.204a.75.75 0 0 0 .74-1.16l-3.874-5.764Z"/></svg>'

const TARGET_CLASS = 'bottle-tour__target'

const describe = step =>
  step.action
    ? `${step.description}<span class="bottle-tour__action">${CURSOR_ICON}<span>${step.action}</span></span>`
    : step.description

const clickableIn = element => (element.matches('a, button') ? element : element.querySelector('a, button') ?? element)

function showMedia(popover, media) {
  popover.wrapper.querySelector('.bottle-tour__media')?.remove()
  if (!media) return
  const image = document.createElement('img')
  Object.assign(image, { className: 'bottle-tour__media', src: media.src, alt: media.alt ?? '' })
  image.width = media.width
  image.height = media.height
  popover.title.before(image)
}

function toDriverStep(step) {
  return {
    element: step.element ? () => findTarget(step) : undefined,
    popover: {
      title: step.title,
      description: describe(step),
      side: step.side,
      align: step.align,
      ...(step.buttons && { showButtons: step.buttons }),
      ...(step.nextLabel && { nextBtnText: step.nextLabel, doneBtnText: step.nextLabel })
    }
  }
}

export function runTour(tour, { source, onFinish } = {}) {
  const steps = tour.skipMissingSteps ? availableSteps(tour) : tour.steps
  const defaultBranch = Object.keys(tour.branches ?? {})[0] ?? null
  let branch = null
  let lastIndex = 0
  let viewedIndex = null
  let completed = false
  let advancing = false
  let detachStep = null
  let trackingFrame = null
  let trackedRect = null
  let highlightedAt = 0
  let placedAfterSettle = false

  const visibleSteps = () => stepsForBranch(steps, branch ?? defaultBranch)
  const event = () => ({ tour_id: tour.id, source, branch, step_count: visibleSteps().length })
  const nextIndexAfter = index => steps.findIndex((step, i) => i > index && (!step.branch || step.branch === branch))

  const followActiveElement = () => {
    trackingFrame = requestAnimationFrame(followActiveElement)
    document
      .querySelectorAll('.driver-overlay[inert], .driver-popover[inert]')
      .forEach(el => el.removeAttribute('inert'))
    const element = tourDriver.getActiveElement()
    const index = tourDriver.getActiveIndex()
    const settled = performance.now() - highlightedAt > HIGHLIGHT_SETTLE_MS
    if (element && !element.isConnected && !advancing && settled && findTarget(steps[index])) {
      return tourDriver.moveTo(index)
    }
    if (settled && !placedAfterSettle) {
      placedAfterSettle = true
      tourDriver.refresh()
    }
    const key = element?.isConnected && rectKey(element)
    if (!key || key === trackedRect) return
    if (settled && trackedRect) tourDriver.refresh()
    trackedRect = key
  }

  const listenForAdvance = (index, element) => {
    const step = steps[index]
    if (step.advanceOnClick && element) {
      const onClick = () => advance({ clicked: true })
      element.addEventListener('click', onClick, true)
      detachStep = () => element.removeEventListener('click', onClick, true)
    }
    if (step.advanceWhenNextAppears) {
      let frame
      const branchPending = () => tour.branches && !branch && steps[index + 1]?.branch
      const watchNext = () => {
        const ready = branchPending() ? chooseBranch(tour) : findTarget(steps[nextIndexAfter(index)])
        if (ready) return advance({ nextAppeared: true })
        frame = requestAnimationFrame(watchNext)
      }
      watchNext()
      detachStep = () => cancelAnimationFrame(frame)
    }
  }

  const advance = async ({ clicked = false, nextAppeared = false }) => {
    const index = tourDriver.getActiveIndex()
    const step = steps[index]
    if (advancing || (step.advanceWhenNextAppears && !nextAppeared)) return
    advancing = true
    detachStep?.()
    const target = findTarget(step) ?? tourDriver.getActiveElement()
    if (step.advanceOnClick && !clicked && target) clickableIn(target).click()
    showWaiting(true)

    const stay = () => {
      advancing = false
      showWaiting(false)
      listenForAdvance(index, findTarget(step) ?? target)
    }

    if (tour.branches && !branch && steps[index + 1]?.branch) {
      await waitUntil(() => chooseBranch(tour))
      branch = chooseBranch(tour)
      if (!branch) return stay()
    }

    const nextIndex = nextIndexAfter(index)
    if (nextIndex === -1) {
      completed = true
      tourDriver.destroy()
      return
    }
    await waitForTarget(steps[nextIndex])
    if (steps[nextIndex].element && !findTarget(steps[nextIndex])) return stay()
    advancing = false
    tourDriver.moveTo(nextIndex)
  }

  const tourDriver = driver({
    steps: steps.map(toDriverStep),
    popoverClass: `bottle-tour bottle-tour--${tour.theme}`,
    overlayColor: tour.overlayColor ?? '#000',
    showButtons: tour.buttons ?? ['next', 'previous', 'close'],
    showProgress: true,
    overlayClickBehavior: () => {},
    allowKeyboardControl: false,
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    overlayOpacity: 0.55,
    stagePadding: 4,
    stageRadius: 8,
    onPopoverRender: (popover, { state }) => {
      const step = steps[state.activeIndex]
      const counted = progressSteps(visibleSteps())
      popover.wrapper.classList.remove('bottle-tour--waiting')
      popover.wrapper.classList.toggle('bottle-tour--modal', !!step.modal)
      showMedia(popover, step.media)
      popover.progress.textContent = `${counted.indexOf(step) + 1} of ${counted.length}`
      popover.progress.style.visibility = step.modal ? 'hidden' : ''
    },
    onHighlighted: (element, _step, { state }) => {
      lastIndex = state.activeIndex
      trackedRect = null
      highlightedAt = performance.now()
      placedAfterSettle = false
      document.querySelectorAll('.driver-active-element').forEach(stale => {
        if (stale !== element) stale.classList.remove('driver-active-element', TARGET_CLASS)
      })
      element?.classList.toggle(TARGET_CLASS, !!steps[lastIndex].action)
      listenForAdvance(lastIndex, element)
      if (viewedIndex === lastIndex) return
      viewedIndex = lastIndex
      posthog.capture('product_tour_step_viewed', {
        ...event(),
        step_index: visibleSteps().indexOf(steps[lastIndex]),
        step_id: steps[lastIndex].id
      })
    },
    onDeselected: element => {
      detachStep?.()
      element?.classList.remove(TARGET_CLASS)
    },
    onNextClick: () => advance({}),
    onDestroyed: () => {
      detachStep?.()
      cancelAnimationFrame(trackingFrame)
      document.querySelectorAll(`.${TARGET_CLASS}`).forEach(target => target.classList.remove(TARGET_CLASS))
      delete document.documentElement.dataset.tourTheme
      activeTour.value = null
      posthog.capture(completed ? 'product_tour_completed' : 'product_tour_dismissed', {
        ...event(),
        step_index: visibleSteps().indexOf(steps[lastIndex]),
        step_id: steps[lastIndex]?.id
      })
      onFinish?.()
    }
  })

  document.documentElement.dataset.tourTheme = tour.theme
  activeTour.value = tour.id
  posthog.capture('product_tour_started', event())
  tourDriver.drive()
  followActiveElement()
  return tourDriver
}

export function useProductTour(tour) {
  const store = useStore()
  const route = useRoute()
  const router = useRouter()
  let activeDriver = null
  let stopFlagListener = null
  let flagsLoadedFor = null
  let starting = false
  let unmounted = false

  const onItsPage = () => !tour.startsOn || tour.startsOn(route)

  const untilItCanStart = (variant, source) =>
    new Promise(resolve => {
      const check = () => {
        if (unmounted || ((source === 'preview' || onItsPage()) && canStart(variant))) return resolve()
        setTimeout(check, START_POLL_MS)
      }
      check()
    })

  const goHome = async () => {
    const home = tour.home?.(store.getters.activePlatform)
    if (home && route.path !== home) await router.push(home).catch(() => {})
  }

  const start = async (source, personId) => {
    const steps = stepsForViewport(tour)
    if (activeDriver || starting || !steps) return
    const variant = { ...tour, steps }
    starting = true
    await goHome()
    await untilItCanStart(variant, source)
    starting = false
    if (unmounted) {
      if (source === 'flag') tourQueue.release()
      return
    }
    activeDriver = runTour(variant, {
      source,
      onFinish: () => {
        if (!inStaffSession()) markTourSeen(tour, personId)
        activeDriver = null
      }
    })
  }

  // Every tour shows a merchant what changed, and a merchant who signed up on
  // their own has only ever seen the dashboard as it is now.
  const requestStart = async personId => {
    if (!onItsPage() || !posthog.isFeatureEnabled(tour.flag) || hasSeenTour(tour, personId)) return
    if (isSetupApplicable(await ensureSetupState((...args) => api.call(...args)))) return
    if (unmounted || !onItsPage()) return
    if (stepsForViewport(tour)) tourQueue.request(tour, () => start('flag', personId))
  }

  const listenForFlag = personId => {
    if (!tour.flag || !posthog.__loaded || inStaffSession() || hasSeenTour(tour, personId)) return
    let listening = false
    stopFlagListener = posthog.onFeatureFlags(() => {
      if (!listening) return
      flagsLoadedFor = personId
      requestStart(personId)
    })
    listening = true
    posthog.reloadFeatureFlags()
  }

  if (tour.startsOn) {
    watch(
      () => route.path,
      () => {
        if (flagsLoadedFor) requestStart(flagsLoadedFor)
      }
    )
  }

  watch(
    () => [store.state.loggedInUser, route.query[PREVIEW_PARAM]],
    ([user, preview]) => {
      if (!user) return
      const personId = currentPersonId(user)
      if (preview === tour.id) {
        const query = { ...route.query }
        delete query[PREVIEW_PARAM]
        router.replace({ query })
        start('preview', personId)
        return
      }
      if (!stopFlagListener) listenForFlag(personId)
    },
    { immediate: true }
  )

  onBeforeUnmount(() => {
    unmounted = true
    stopFlagListener?.()
    activeDriver?.destroy()
  })
}
